import scrollManager from '../utils/scroll-manager';
import { SlimSelectHTMLElement } from '../utils/slim-select';

export default function initArchiveRelaunchPlan() {
    const $filtersForm = document.querySelector<HTMLFormElement>('.js-relaunch-plan-filters-form');

    if ($filtersForm) {
        new FiltersForm($filtersForm);
    }
}

type StateFiltersType = { [key: string]: string[] };
type StateType = { page: string, filters: StateFiltersType, search: string };
type CacheItemType = { state: StateType, html: string };

class FiltersForm {
    $form: HTMLFormElement;
    $selects: NodeListOf<SlimSelectHTMLElement>;
    $resultsContainer: HTMLElement;
    getResultsAbortController: AbortController|null = null;
    cache: CacheItemType[] = [];
    onChangeDisabled = false;

    constructor($form: HTMLFormElement) {
        this.$form = $form;
        this.$selects = this.$form.querySelectorAll<SlimSelectHTMLElement>('select.js-slimselect.mounted');
        this.$resultsContainer = document.querySelector<HTMLElement>('.js-relaunch-plan-results') as HTMLElement;

        // Mise en cache de la page
        const state = this.createState(new URLSearchParams(window.location.search));
        this.cache.push({ state, html: this.$resultsContainer.innerHTML });
        window.history.replaceState(state, '', state.search ? '?' + state.search : '');

        // Lorsqu'on change les filtres
        this.$selects.forEach($select => {
            $select.addEventListener('change', () => {
                if (!this.onChangeDisabled) {
                    this.submitForm(null, '1');
                }
            });
        });

        // Lorsqu'on navigue dans l'historique du navigateur
        window.addEventListener('popstate', event => {
            if (!this.onChangeDisabled) {
                this.submitForm(event.state, event.state.page);
            }
        });

        this.initResultsEvents();
    }

    /**
     * Requête ajax lors de la navigation via la pagination
     * 
     * @return {void}
     */
    initResultsEvents() : void {
        this.$resultsContainer.querySelectorAll<HTMLAnchorElement>('.pagination a').forEach($link => {
            $link.addEventListener('click', event => {
                event.preventDefault();

                // Récupération du numéro de page via l'URL du lien sur lequel on a cliqué
                const url = (new URL($link.href)).pathname;
                const pathnameParts = url.split('/');
                let nextPage = typeof pathnameParts[pathnameParts.length - 2] !== 'undefined' ? pathnameParts[pathnameParts.length - 2] : '1';

                if (isNaN(parseInt(nextPage))) {
                    nextPage = '1';
                }

                this.submitForm(null, nextPage);
            });
        });
    }

    /**
     * Soumet le formulaire en ajax et affiche les résultats
     * 
     * @param {StateType|null} state
     * @param {string|null} page
     * 
     * @return {void}
     */
    submitForm(state: StateType|null = null, page: string|null = null) : void {
        const data = new URLSearchParams(state?.search || '');

        // Scroll jusqu'au formulaire
        const adminBarHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--admin-bar-height'));
        const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'));
        const scrollOffset = adminBarHeight + headerHeight + 50;
        scrollManager.scrollTo(this.$form, true, scrollOffset, 'down');

        // Récupération des valeurs sélectionnées et construction des paramètres d'URL
        this.$selects.forEach($select => {
            const selectName = $select.getAttribute('name') as string;
            const value = state !== null ? data.getAll(selectName) : $select.slim.selected();

            if (state !== null) {
                this.onChangeDisabled = true;
                $select.slim.set(value);
                this.onChangeDisabled = false;
            }

            if (value instanceof Array && value.length > 0) {
                value.forEach(val => {
                    data.append(selectName, val);
                });
            } else if (!(value instanceof Array)) {
                data.append(selectName, value);
            }
        });

        // Récupération de l'url de base de la page (sans le numéro de page etc..)
        let url = window.location.pathname.replace(/page\/([0-9]+)\//, '');

        if (state === null) {
            state = this.createState(data, page);

            // Ajout de la page et des paramètres GET dans l'url
            url += (page && page !== '1' ? `page/${page}/` : '');
            url += data.toString().length > 0 ? `?${data}` : '';

            // Changement de l'URL dans le navigateur
            window.history.pushState(state, '', url);
        }

        const newState = state;

        // Annulation de la requête précédente s'il y en a encore une qui tourne
        if (this.getResultsAbortController) {
            this.getResultsAbortController.abort();
        }

        // Recherche des résultats dans le cache
        const cachedItem = this.searchInCache(newState);
        if (cachedItem) {
            this.showResults(cachedItem.html);
            return;
        }

        this.getResultsAbortController = new AbortController();

        this.$resultsContainer.classList.add('loading');

        fetch(url, {
            method: 'GET',
            credentials: 'same-origin',
            signal: this.getResultsAbortController.signal,
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
        }).then(response => response.text()).then(content => {
            this.showResults(content);

            // Enregistrement des résultats dans le cache
            this.cache.push({ state: newState, html: content });
        }).finally(() => {
            this.getResultsAbortController = null;

            this.$resultsContainer.classList.remove('loading');
        }).catch(() => {
            // Retour à la page précédente
            this.onChangeDisabled = true;
            window.history.back();
            this.onChangeDisabled = false;

            return;
        });
    }

    /**
     * Affiche les résultats sur la page
     * 
     * @param {string} html 
     * 
     * @return {void}
     */
    showResults(html: string) : void
    {
        this.$resultsContainer.innerHTML = html;
        this.initResultsEvents();
    }

    /**
     * Créé le state de la page
     * 
     * @param {URLSearchParams} data 
     * @param {string|null} page 
     * 
     * @return {StateType}
     */
    createState(data: URLSearchParams, page: string|null = null) : StateType {
        page = page || this.$resultsContainer.querySelector<HTMLSpanElement>('.pagination [aria-current="page"]')?.innerText || '1';
        const filters: StateFiltersType = {};

        for (const pair of data.entries()) {
            if (typeof filters[pair[0]] === 'undefined') {
                filters[pair[0]] = [];
            }

            filters[pair[0]].push(pair[1]);
        }

        return { page, filters, search: data.toString() };
    }

    /**
     * Recherche un state dans le cache
     * 
     * @param {StateType} state 
     * 
     * @return {CacheItemType|null}
     */
    searchInCache(state: StateType) : CacheItemType|null
    {
        let founded: CacheItemType|null = null;

        this.cache.forEach(cacheItem => {
            if (founded) {
                return;
            }

            const samePage = cacheItem.state.page === state.page;
            const filtersMatching = Object.keys(state.filters).length ? Object.keys(state.filters).filter(filterKey => {
                return typeof cacheItem.state.filters[filterKey] !== 'undefined' &&
                    cacheItem.state.filters[filterKey].length === state.filters[filterKey].length &&
                    cacheItem.state.filters[filterKey].sort().every((value, index) => value === state.filters[filterKey].sort()[index]);
            }) : [];

            if (samePage && filtersMatching.length === Object.keys(state.filters).length && Object.keys(cacheItem.state.filters).length === Object.keys(state.filters).length) {
                founded = cacheItem;
            }
        });

        return founded;
    }
}
